import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Loader2, SlidersHorizontal } from 'lucide-react';
import {
  DEFAULT_ACWR_MAX,
  DEFAULT_ACWR_MIN,
  defaultForAttributeType,
  sortPlanningBlockTypes,
  typesForPlanningScope,
} from '../../utils/periodizationPlanningScope';
import AttributeValueEditor from './AttributeValueEditor';

function attrLabel(t, typeKey, fallback) {
  const k = `periodization.attributes.${typeKey}`;
  const tr = t(k);
  return tr !== k ? tr : fallback || typeKey;
}

export default function BlockPlanValuesPanel({ tree, onUpdated, onConfigureParameters }) {
  const { t } = useLanguage();
  const allTypes = tree?.assessment_set?.attribute_types || [];
  const block = tree?.block;
  const scopeOverrides = block?.planning_scope_overrides;

  const [localAttrs, setLocalAttrs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocalAttrs({});
  }, [block?.id, block?.attributes]);

  const blockAttrs = useMemo(
    () => ({ ...(block?.attributes || {}), ...localAttrs }),
    [block?.attributes, localAttrs]
  );

  const blockTypes = useMemo(
    () => sortPlanningBlockTypes(typesForPlanningScope(allTypes, scopeOverrides, 'block')),
    [allTypes, scopeOverrides]
  );

  const save = useCallback(async () => {
    if (!block?.id) return;
    setSaving(true);
    try {
      const attrsToSave = { ...blockAttrs };
      if (attrsToSave.acwr_max == null && blockTypes.some((ty) => ty.type_key === 'acwr_max')) {
        attrsToSave.acwr_max = DEFAULT_ACWR_MAX;
      }
      if (attrsToSave.acwr_min == null && blockTypes.some((ty) => ty.type_key === 'acwr_min')) {
        attrsToSave.acwr_min = DEFAULT_ACWR_MIN;
      }
      const res = await api.put(`/api/training/periodization/blocks/${block.id}`, {
        attributes: attrsToSave,
      });
      setLocalAttrs({});
      onUpdated?.(res.data);
    } catch (e) {
      window.alert(e.response?.data?.error || t('periodization.planScope.saveError'));
    } finally {
      setSaving(false);
    }
  }, [block?.id, blockAttrs, blockTypes, onUpdated, t]);

  if (!block) return null;

  return (
    <div className='space-y-3 rounded-lg border border-border p-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h3 className='text-sm font-semibold'>{t('periodization.planScope.planValuesTitle')}</h3>
          <p className='text-xs text-muted-foreground'>{t('periodization.planScope.planValuesHint')}</p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Button type='button' size='sm' variant='outline' className='gap-1.5' onClick={onConfigureParameters}>
            <SlidersHorizontal className='h-3.5 w-3.5' />
            {t('periodization.planScope.configureParameters')}
          </Button>
          <Button type='button' size='sm' onClick={save} disabled={saving}>
            {saving ? <Loader2 className='h-4 w-4 animate-spin' /> : t('periodization.planScope.saveValues')}
          </Button>
        </div>
      </div>

      {blockTypes.length > 0 ? (
        <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
          {blockTypes.map((type) => (
            <div key={type.type_key} className='rounded-md border border-border p-3 space-y-2'>
              <Label className='text-xs'>{attrLabel(t, type.type_key, type.name)}</Label>
              <AttributeValueEditor
                type={type}
                value={blockAttrs[type.type_key] ?? defaultForAttributeType(type) ?? undefined}
                onChange={(v) => setLocalAttrs((prev) => ({ ...prev, [type.type_key]: v }))}
              />
            </div>
          ))}
        </div>
      ) : (
        <p className='text-xs text-muted-foreground'>{t('periodization.planScope.noBlockParams')}</p>
      )}
    </div>
  );
}
